/**
 * Prenotazioni Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/prenotazioni';
import type { Prenotazione, PrenotazioneInsert, PrenotazioneUpdate } from '@/types';
import type { PrenotazioneFilters } from './types';

const QUERY_KEY = 'prenotazioni';

// ============================================================
// GET Queries
// ============================================================

export function usePrenotazioni(filters?: PrenotazioneFilters) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'list', user?.id, filters],
    queryFn: async () => {
      const { data, error } = await api.getPrenotazioni({ 
        userId: user?.id,
        ...filters 
      });
      if (error) throw error;
      return (data || []) as Prenotazione[];
    },
    enabled: !!user?.id,
  });
}

export function usePrenotazione(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getPrenotazioneById(id, user?.id);
      if (error) throw error;
      return data as Prenotazione | null;
    },
    enabled: !!id && !!user?.id,
  });
}

export function usePrenotazioniAttive() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'attive', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getPrenotazioniAttive(user?.id);
      if (error) throw error;
      return (data || []) as Prenotazione[];
    },
    enabled: !!user?.id,
  });
}

export function usePrenotazioniInArrivo(giorni: number = 7) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'inarrivo', giorni, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getPrenotazioniInArrivo(giorni, user?.id);
      if (error) throw error;
      return (data || []) as Prenotazione[];
    },
    enabled: !!user?.id,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useCreatePrenotazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (prenotazione: PrenotazioneInsert) => {
      const { data, error } = await api.createPrenotazione(prenotazione, user?.id);
      if (error) throw error;
      return data as Prenotazione;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdatePrenotazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PrenotazioneUpdate }) => {
      const { data: result, error } = await api.updatePrenotazione(id, data, user?.id);
      if (error) throw error;
      return result as Prenotazione;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeletePrenotazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deletePrenotazione(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useAggiornaStatoPrenotazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, stato }: { id: string; stato: string }) => {
      const { data, error } = await api.updatePrenotazione(id, { stato }, user?.id);
      if (error) throw error;
      return data as Prenotazione;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

// ============================================================
// Stats
// ============================================================

export function usePrenotazioniStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { data: prenotazioni, error } = await api.getPrenotazioni({ userId: user?.id });
      if (error) throw error;
      
      const p = prenotazioni || [];
      const oggi = new Date().toISOString().split('T')[0];
      
      return {
        totali: p.length,
        attive: p.filter((x: Prenotazione) => x.stato === 'confermata').length,
        inArrivo: p.filter((x: Prenotazione) => x.check_in && x.check_in >= oggi).length,
        completate: p.filter((x: Prenotazione) => x.stato === 'completata').length,
        cancellate: p.filter((x: Prenotazione) => x.stato === 'cancellata').length,
      };
    },
    enabled: !!user?.id,
  });
}
